
/**
 * Utility functions for cookie management
 */

import { v4 as uuidv4 } from 'uuid';
import { Message } from '@/types/chat';

const VISITOR_ID_COOKIE = 'goldcrusade_visitor_id';
const CHAT_HISTORY_COOKIE = 'goldcrusade_chat_history';
const COOKIE_EXPIRY_DAYS = 7; // Cookie will last for 1 week

/**
 * Get or create a unique visitor ID
 * @returns The visitor's UUID
 */
export const getOrCreateVisitorId = (): string => {
  let visitorId = getCookie(VISITOR_ID_COOKIE);
  
  if (!visitorId) {
    visitorId = uuidv4();
    setCookie(VISITOR_ID_COOKIE, visitorId, COOKIE_EXPIRY_DAYS);
  }
  
  return visitorId;
};

/**
 * Set a cookie with the given name, value and expiry days
 */
export const setCookie = (name: string, value: string, days: number): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

/**
 * Get a cookie value by name
 * @returns The cookie value or empty string if not found
 */
export const getCookie = (name: string): string => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return '';
};

/**
 * Delete a cookie by name
 */
export const deleteCookie = (name: string): void => {
  setCookie(name, '', -1);
};

/**
 * Save chat history to cookie
 * @param messages Array of chat messages to save
 */
export const saveChatHistory = (messages: Message[]): void => {
  try {
    // We need to convert File objects to just their names since Files can't be serialized
    const serializableMessages = messages.map(msg => ({
      ...msg,
      files: msg.files ? msg.files.map(file => file.name) : undefined
    }));
    
    const serialized = JSON.stringify(serializableMessages);
    setCookie(CHAT_HISTORY_COOKIE, serialized, COOKIE_EXPIRY_DAYS);
  } catch (error) {
    console.error('Failed to save chat history:', error);
  }
};

/**
 * Load chat history from cookie
 * @returns Array of chat messages or empty array if none found
 */
export const loadChatHistory = (): Message[] => {
  try {
    const serialized = getCookie(CHAT_HISTORY_COOKIE);
    if (!serialized) return [];
    
    // Parse the serialized messages, but note that File objects won't be restored
    // They will just have the filename strings
    return JSON.parse(serialized);
  } catch (error) {
    console.error('Failed to load chat history:', error);
    return [];
  }
};

/**
 * Clear chat history from cookie
 */
export const clearChatHistory = (): void => {
  deleteCookie(CHAT_HISTORY_COOKIE);
};
